import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const StudentFeedback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    type: 'complaint',
    category: 'general',
    subject: '',
    message: '',
    anonymous: false
  });

  useEffect(() => {
    if (student) {
      loadFeedback();
    }
  }, [student]);

  const loadFeedback = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/feedback/${student.username}`);
      const feedbackData = await response.json();
      setFeedback(feedbackData);
    } catch (error) {
      console.error('Error loading feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.message.trim()) {
      alert('Please fill in both subject and message');
      return;
    }

    try {
      const feedbackData = {
        studentUsername: student.username,
        studentName: `${student.firstName} ${student.lastName}`,
        className: student.className,
        ...formData
      };

      const response = await fetch(`${API_ENDPOINTS.FEEDBACK}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData)
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          type: 'complaint',
          category: 'general',
          subject: '',
          message: '',
          anonymous: false
        });
        setShowForm(false);

        // Reload feedback to show the new submission
        loadFeedback();

        // Hide success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      } else {
        alert('Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    }
  };

  const getFilteredFeedback = () => {
    if (selectedCategory === 'all') return feedback;
    return feedback.filter(item => item.category === selectedCategory);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    return type === 'complaint' ? 'Warning' : 'Idea';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'facilities': return 'Facilities';
      case 'academic': return 'Books';
      case 'activities': return 'Goals';
      case 'food': return 'Food';
      case 'transport': return 'Transport';
      case 'general': return 'Note';
      default: return 'Note';
    }
  };

  if (!student) {
    navigate('/student-login');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading feedback...</div>
      </div>
    );
  }

  const filteredFeedback = getFilteredFeedback();

  return (
    <div className="min-h-screen bg-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-blue-900">Feedback & Suggestions</h1>
              <p className="text-sm text-gray-600">Share your thoughts and help improve our school</p>
            </div>
            <button
              onClick={() => navigate('/student-dashboard', { state: { student } })}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="text-green-500 mr-2">OK</div>
              <p className="text-green-700">Your feedback has been submitted successfully!</p>
            </div>
          </div>
        )}

        {/* Submit Feedback Button */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Share Your Thoughts</h2>
              <p className="text-gray-600">Have a complaint or suggestionLoading We'd love to hear it!</p>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200 flex items-center space-x-2"
            >
              <span>{showForm ? '−' : '+'}</span>
              <span>{showForm ? 'Cancel' : 'Submit Feedback'}</span>
            </button>
          </div>

          {/* Feedback Form */}
          {showForm && (
            <form onSubmit={handleSubmit} className="mt-6 border-t pt-6 space-y-6">
              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  What type of feedback is thisLoading
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="type"
                      value="complaint"
                      checked={formData.type === 'complaint'}
                      onChange={handleFormChange}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Warning Complaint</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="type"
                      value="idea"
                      checked={formData.type === 'idea'}
                      onChange={handleFormChange}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Idea Suggestion</span>
                  </label>
                </div>
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="general">Note General</option>
                  <option value="facilities">Facilities Facilities</option>
                  <option value="academic">Books Academic</option>
                  <option value="activities">Goals Activities</option>
                  <option value="food">Food Food & Nutrition</option>
                  <option value="transport">Transport Transportation</option>
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleFormChange}
                  placeholder="Brief summary of your feedback..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleFormChange}
                  rows="6"
                  placeholder="Please provide detailed information about your feedback..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Anonymous Option */}
              <div>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="anonymous"
                    checked={formData.anonymous}
                    onChange={handleFormChange}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">Submit anonymously (your identity will be hidden from public view)</span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 font-medium"
                >
                  Submit Feedback
                </button>
              </div>
            </form>
          )}
        </div>

        {/* My Feedback History */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">My Feedback History</h2>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                <option value="general">General</option>
                <option value="facilities">Facilities</option>
                <option value="academic">Academic</option>
                <option value="activities">Activities</option>
                <option value="food">Food</option>
                <option value="transport">Transport</option>
              </select>
            </div>
          </div>

          <div className="p-6">
            {filteredFeedback.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">Inbox</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No Feedback Yet</h3>
                <p className="text-gray-600 mb-6">
                  {selectedCategory === 'all'
                    ? "You haven't submitted any feedback yet. Share your thoughts to help improve our school!"
                    : `No ${selectedCategory} feedback found. Try selecting a different category.`}
                </p>
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                >
                  Submit Your First Feedback
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFeedback.map((item) => (
                  <div key={item.id} className="border rounded-lg p-4 hover:shadow-md transition duration-200">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getTypeIcon(item.type)}</span>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{item.subject}</h3>
                          <div className="flex items-center space-x-3 text-sm text-gray-600">
                            <span className="flex items-center space-x-1">
                              <span>{getCategoryIcon(item.category)}</span>
                              <span>{item.category}</span>
                            </span>
                            <span>?</span>
                            <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                            {item.anonymous && <span className="text-purple-600">(Anonymous)</span>}
                          </div>
                        </div>
                      </div>

                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </div>

                    <p className="text-gray-700 mb-3 leading-relaxed">{item.message}</p>

                    {/* Admin Response */}
                    {item.adminResponse && (
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-blue-600 font-medium">👨‍School Admin Response</span>
                          <span className="text-xs text-gray-500">
                            {new Date(item.respondedAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-blue-800">{item.adminResponse}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentFeedback;




